import { ModelClass } from '@model/Model';
import { AxiosPromise, AxiosResponse } from 'axios';
import { isImmutable, List, Map } from 'immutable';
import { getState } from './state';
import { ActionObject, PaginationResponse } from './types';

/**
 * Create an empty promise that finish after given `ms` number
 * @param ms
 * @return {Promise}
 */
export const delay = (ms: number): Promise<any> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Create simple action
 * @param type
 * @param _payload
 */
export const simpleAction =
    <T = any>(type: string, _payload?: T): ((payload?: T) => ActionObject<T, string>) =>
    (payload?: T): ActionObject<T, string> => ({
        type,
        payload: typeof payload !== 'undefined' ? payload : (_payload as any),
    });

/**
 * Useful state set data
 * @param state
 * @param data
 * @returns {string}
 * @private
 */
const _setElements = (state: any, data: { [key: string]: any }) => state.merge(Map(data));

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnGetRequest =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload: params }: any) =>
        _setElements(state, {
            params,
            busy: true,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnGetSuccess =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) => {
        const { data, _params: params } = payload;
        const { items, pagination } = data;

        return _setElements(state, {
            items: items,
            pages: pagination.pages,
            params: params,
            total: pagination.total,
            cursor: pagination.cursor,
            busy: false,
        });
    };

export const simpleOnGetFail =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            error: payload.response,
            items: [],
            pages: 0,
            total: 0,
            busy: false,
        });

export const simpleOnGetDone =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE) =>
        _setElements(state, {
            error: null,
            items: null,
            pages: 0,
            total: 0,
            busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemRequest =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload: params }: any) =>
        _setElements(state, {
            item: null,
            item_busy: true,
            params,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemSuccess =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            item: payload.data,
            item_busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemFail =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            item: null,
            error: payload.response,
            item_busy: false,
        });

/**
 * Aliases
 */
export const simpleOnItemLoadRequest = simpleOnItemRequest;
export const simpleOnItemLoadSuccess = simpleOnItemSuccess;
export const simpleOnItemLoadFail = simpleOnItemFail;

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemCreateRequest =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload: params }: any) =>
        _setElements(state, {
            item_create_busy: true,
            params,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemCreateSuccess =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            item_created: payload.data,
            item_create_busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemCreateFail =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            error: payload.response,
            item_created: null,
            item_create_busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemCreateDone =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE) =>
        _setElements(state, {
            error: null,
            item_created: null,
            item_create_busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemUpdateRequest =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            item_update_busy: true,
            params: payload,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemUpdateSuccess =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            item_update_busy: false,
            item_updated: payload.data,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemUpdateFail =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            item_updated: null,
            error: payload.response.data,
            item_update_busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemUpdateDone =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE) =>
        _setElements(state, {
            item_updated: null,
            error: null,
            item_update_busy: false,
        });
/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemDestroyRequest =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            item_destroy_busy: true,
            params: payload,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemDestroySuccess =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE) =>
        _setElements(state, {
            item_destroy_busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemDestroyFail =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE, { payload }: any) =>
        _setElements(state, {
            error: payload.response.data,
            item_destroy_busy: false,
        });

/**
 *
 * @param DEFAULT_STATE
 * @returns {function(state:any, action: {payload: any}): any}
 */
export const simpleOnItemDestroyDone =
    (DEFAULT_STATE: any) =>
    (state: any = DEFAULT_STATE) =>
        _setElements(state, {
            error: null,
            item_destroy_busy: false,
        });

/**
 * Load or request
 * Load from redux or request.
 * @param items
 * @param id
 * @param request
 * @returns {*}
 */
export async function loadOrRequest<T = any>(
    items: string | T[],
    id: number,
    request: (id: number) => AxiosPromise<T>,
): Promise<AxiosResponse<T>> {
    const id$: number = parseInt(`${id}`);
    const validID = !Number.isNaN(id$);

    if (validID && typeof items === 'string') {
        // Retrieve Redux collection
        items = getState()[items].get('items');
    }

    if (items && items.length > 0 && validID) {
        const item: T = (items as any).find((item: any) => parseInt(item.get(id)) === id$);

        if (item) {
            return {
                item,
                items,
            } as any;
        }
    }

    const item$: AxiosResponse<T> = await request(id$);

    const item: T = item$.data;
    const found: T = (items as any).find((item: any) => parseInt(item.get(id)) === id$);

    if (!found) {
        if (!isImmutable(items)) {
            items = List(items as any) as any;
        }

        items = (items as any).push(item);
    }

    return { item, items } as any;
}

/**
 *
 * @param response
 * @param model
 * @returns {AxiosResponse<PaginationResponse<T>>}
 */
export function fillItems<T = any>(
    response: AxiosResponse<PaginationResponse<T>>,
    model: ModelClass,
): AxiosResponse<PaginationResponse<T>> {
    const { data } = response;

    if (data && data.items) {
        data.items = data.items.map((data) => new (model as any)().$fill(data));

        response.data = data;
    }

    return response;
}

export function* afterAction(payload: any): any {
    const { _after } = payload || {};

    if (_after && typeof _after === 'function') {
        yield _after();
    }
}
